import {
  MutableRefObject,
  useEffect,
  useState,
  useRef,
  ReactNode,
  forwardRef,
  useImperativeHandle,
  ChangeEventHandler,
} from 'react';
import classNames from 'classnames/bind';
import { FAIcon, IconStyle } from '../Icons/Icons';
import { MLPVersion } from '../../types/enums';
import Tooltip from '../Tooltip/Tooltip';
import { useClickOutsideDetector } from '../../hooks';
import styles from './Search.module.scss';

import { Data, SearchPropsTypes, SearchTheme } from './types/propsType';

const cx = classNames.bind(styles);

const getDisabledClass = (disabled: boolean) => (disabled ? styles['disabled-container'] : '');

const getDrawerDisplayClass = (drawerDisplay: boolean, disabled: boolean) =>
  drawerDisplay && !disabled ? styles['open-search-result'] : '';
const getIsMLP2Variant = (isMLP2Variant: boolean, onClick: React.MouseEventHandler<HTMLSpanElement> | undefined) =>
  isMLP2Variant && (
    <FAIcon
      iconName={'magnifying-glass'}
      className={`${styles['icon']} ${styles['search-mlp2-left-icon']}`}
      data-testid="search-mlp2-icon-test-left-id"
      onClick={onClick}
    />
  );

const getMagnifyingGlass = (
  isMLP2Variant: boolean,
  value: string | number,
  isTyping: boolean,
  onClick: React.MouseEventHandler<HTMLSpanElement> | undefined,
) =>
  !isMLP2Variant &&
  (!value || isTyping) && (
    <span>
      <FAIcon
        iconName={'magnifying-glass'}
        iconStyle={IconStyle.SOLID}
        className={`${styles['icon']} ${styles['icon-desktop']}`}
        data-testid="search-icon-test-right-id"
        onClick={onClick}
      />
    </span>
  );

const getCloseXMark = (
  isMLP2Variant: boolean,
  value: string | number,
  isTyping: boolean,
  onClick: React.MouseEventHandler<HTMLSpanElement> | undefined,
) =>
  value &&
  (!isTyping || isMLP2Variant) && (
    <span className={styles['close-icon']}>
      <FAIcon
        iconName={isMLP2Variant ? 'close' : 'xmark-large'}
        iconStyle={IconStyle.SOLID}
        className={`${styles['icon']} ${!isMLP2Variant ? styles['icon-medium'] : ''}`}
        data-testid="clear-icon-test-id"
        onClick={(e) => onClick?.(e)}
      />
    </span>
  );

/**
 * @description Provides Search Field
 */
const Search = forwardRef<HTMLSpanElement, SearchPropsTypes>(
  (
    {
      value,
      selectionActiveState = false,
      data = [],
      placeholder = '',
      disabled = false,
      onChange,
      onSearch,
      configData,
      onClear,
      className = '',
      mlpVersion = MLPVersion.ONE,
      label,
      labelInfo,
      theme,
      message,
      ...restProps
    }: SearchPropsTypes,
    ref,
  ) => {
    const searchRef = useRef() as MutableRefObject<HTMLSpanElement>;
    const searchIconRef = useRef() as MutableRefObject<HTMLSpanElement>;
    const [drawerDisplay, setDrawerDisplay] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const isMLP2Variant = mlpVersion === MLPVersion.TWO;
    useImperativeHandle(ref, () => searchRef.current);
    useEffect(() => {
      setDrawerDisplay(data.length > 0);
    }, [data]);

    useClickOutsideDetector(searchRef, () => {
      setDrawerDisplay(false);
      setIsTyping(false);
    });
    const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
      setIsTyping(true);
      onChange(e);
    };
    const CClassname = cx('search-container', [theme], className);
    const CMainClassname = cx('search-box', {
      'search-field-result': drawerDisplay && !disabled,
      'search-mlp2': isMLP2Variant,
    });
    return (
      <section className={CClassname} id="nextgen-search-component">
        {label && !isMLP2Variant && (
          <section className={styles['search-field-label-container']}>
            <label htmlFor={restProps.id} className={styles['search-field-label']}>
              {label}
            </label>
            {restProps?.required && <sup>*</sup>}
            {labelInfo && <Tooltip message={labelInfo} />}
          </section>
        )}
        <span className={`${styles['search-body-container']} ${getDisabledClass(disabled)}`} ref={searchRef}>
          {/* Search Box with Icon */}
          <section className={CMainClassname}>
            {getIsMLP2Variant(isMLP2Variant, onSearch)}
            <input
              value={value}
              onChange={handleChange}
              className={`${styles['search-field']}`}
              placeholder={placeholder}
              onKeyDown={(e) => {
                e.key === 'Enter' && onSearch?.();
              }}
              onFocus={() => {
                setDrawerDisplay(data.length > 0 || false);
              }}
              disabled={disabled}
              data-testid="search-test-id"
              autoComplete="false"
              {...restProps}
            />
            <span ref={drawerDisplay ? null : searchIconRef}>
              {getMagnifyingGlass(isMLP2Variant, value, isTyping, onSearch)}
              {getCloseXMark(isMLP2Variant, value, isTyping, (e) => {
                onClear?.(e);
                setDrawerDisplay(false);
              })}
            </span>
          </section>
          {/* Search Result Drawer */}
          {!isMLP2Variant && (
            <section className={`${styles['base-search-result']} ${getDrawerDisplayClass(drawerDisplay, disabled)}`}>
              <ul>
                {data.map((item: Data, index) => (
                  <li
                    title={`${item[configData?.label as string]}`}
                    role="presentation"
                    key={`search${+index}`}
                    className={`${styles[`selected-item-${item.selected}`]} ${
                      styles[`hover-item-active-state-${selectionActiveState}`]
                    }`}
                    onClick={() => {
                      configData?.onSelect(item);
                      setTimeout(() => {
                        setDrawerDisplay(false);
                      }, 0);
                    }}
                    onKeyDown={() => {
                      configData?.onSelect(item);
                      setTimeout(() => {
                        setDrawerDisplay(false);
                      }, 0);
                    }}
                    data-testid="search-result-testid"
                  >
                    <p>{item[configData?.label as string] as ReactNode}</p>
                  </li>
                ))}
              </ul>
            </section>
          )}
          {message && <span className={styles['search-message']}>{message}</span>}
        </span>
      </section>
    );
  },
);
Search.displayName = 'Search';
export type { SearchTheme, Data };
export default Search;
